import {
  type ImpersonatorSession,
  type LogoutReason,
  type UserSession,
} from '@solinkcorp/authentication';
import { DEFAULT_LOCALE } from '@solinkcorp/vd-common';
import { type AnalyticsEvent } from './common';
import * as TrackingEvents from './coreAnalyticsEvents';
import { _disableIntercom, _loadIntercom, initIntercom, trackEventIntercom } from './intercom';
import { _loadMixpanel, initMixpanel, trackMixpanelEvent, trackUser } from './mp';

export { trackUser, trackUserPreferenceChange } from './mp';

/**
 * Track an analytics event.
 */
export function trackEvent(event: Readonly<AnalyticsEvent>): void {
  trackEventIntercom(event);
  trackMixpanelEvent(event);
}

/**
 * Do initial setup of mixpanel and intercom.
 */
async function setupTrackers(user: UserSession, impersonator: ImpersonatorSession | undefined) {
  const [intercom, mixpanel] = await Promise.all([
    _loadIntercom(user, impersonator),
    _loadMixpanel(),
  ]);

  initIntercom(intercom, user, impersonator);
  await initMixpanel(mixpanel, user, impersonator);
}

export function trackLogin(
  user: UserSession | ImpersonatorSession,
  impersonator: ImpersonatorSession | undefined,
): void {
  if (user.type === 'impersonator') {
    return;
  }

  setupTrackers(user, impersonator)
    .then(() => {
      trackEvent(TrackingEvents.LOGIN);

      const {
        defaultToHD,
        timeFormat,
        defaultVideoFormat,
        widgetRefreshEnabled,
        showEventProgressInVideoPlayer,
        show360DewarpToggle,
        default360ViewMultiplay,
        defaultHeatmapWindow,
        eventListLayoutType,
        locale,
      } = user.profile.preferences || ({} as any);

      const properties = {
        preferenceDefaultToHD: defaultToHD || false,
        preferenceTimeFormat: timeFormat || '', //getDefaultTimeFormat(),
        preferenceDownloadFormat: defaultVideoFormat || 'mp4',
        preferenceWidgetRefreshEnabled: widgetRefreshEnabled || false,
        preferenceShowEventProgressInVideoPlayer: showEventProgressInVideoPlayer || false,
        preferenceShow360DewarpToggle: show360DewarpToggle || false,
        preferenceDefault360ViewMultiplay: default360ViewMultiplay || 'dewarped',
        preferenceHeatmapInterval: `${defaultHeatmapWindow || 1} hours`,
        preferenceEventListLayoutType: eventListLayoutType || 'PEEK',
        preferenceLocale: locale || DEFAULT_LOCALE,
        lastLogin: new Date().toISOString(),
      };

      trackUser({
        properties,
        incrementProperties: {
          numberOfLogins: 1,
        },
      });
    })
    .then(trackExternalLink)
    .catch((err) => console.error(err));
}

function trackExternalLink() {
  const location = window.location;
  // A URL is considered an "external link" when it has query string parameters
  if (location.search) {
    const url = location.href;
    const queryParams = new URLSearchParams(location.search);
    const source = queryParams.get('source');

    trackEvent({
      ...TrackingEvents.EXTERNAL_LINK_LOAD,
      properties: {
        source,
        url,
      },
    });
  }
}

export function trackRefreshSession(
  user: UserSession | ImpersonatorSession,
  impersonator: ImpersonatorSession | undefined,
): void {
  if (user.type === 'impersonator') {
    return;
  }

  setupTrackers(user, impersonator)
    .then(() => trackEvent({ ...TrackingEvents.LOGIN_SESSION }))
    .then(() => trackExternalLink())
    .catch((err) => console.error(err));
}

export function trackLogout(
  reason: LogoutReason,
  errorData?: { source: string; message: string },
): void {
  trackEvent({
    ...TrackingEvents.LOGOUT,
    properties: {
      reason,
      ...errorData,
    },
  });

  _disableIntercom();
}

export async function trackImpersonationReason(
  user: UserSession,
  impersonator: ImpersonatorSession,
  reason: string,
): Promise<void> {
  await setupTrackers(user, impersonator);

  const properties = {
    impersonatorEmail: impersonator.profile.email,
    impersonatorId: impersonator.profile.id,
    imperonsatorUserType: impersonator.profile.userType,
    userEmail: user.profile.email,
    userId: user.profile.id,
    userUserType: user.profile.userType,
    reason,
  };

  await trackEvent({ ...TrackingEvents.IMPERSONATION_REASON, properties });
}

export function trackErrorBoundary(
  error: Error,
  componentStack: string | null | undefined,
  additionalInfo?: Record<string, string>,
): void {
  trackEvent({
    ...TrackingEvents.ERROR_BOUNDARY,
    properties: {
      error: error?.toString(),
      errorStack: error?.stack,
      componentStack,
      ...additionalInfo,
    },
  });
}
